<template>
  <div class="login margin-bottom-l margin-top-l">
    <div class="login__bg"></div>
    <div class="login__content">
      <div class="login__brand">
        <div class="logo-square-wrapper">
          <login-logo-mobile class="logo-square" />
        </div>
      </div>

      <div class="login__form">
        <login-logo-desktop class="logo-horizontal" />
        <h1 class="h-title">{{ $t('health.login.title') }}</h1>
        <p>{{ $t('login.subtitle') }}
          <span
            style="cursor:pointer;"
            v-if="hasSaml()"
            @click="formLogin = !formLogin">
            &#128737;
          </span>
        </p>

        <login-form v-on:createAccount="toggleShowLogin" v-show="showLogin"/>
        <signin-form v-on:login="toggleShowLogin" v-show="!showLogin"/>

        <div class="login__help" v-show="showLogin">
          <div class="login__help-pass">
            <font-awesome-icon :icon="question" />
            <span href=''>{{ $t('login.help') }}</span>
          </div>
          <p
            class="note"
            v-html="$t('login.contact')">
          </p>
          <p
            v-has-route="'/password/reset'"
            class="note"
            v-html="$t('login.resetPassword')">
          </p>
        </div>
        <div class="language-switcher">
          <font-awesome-icon :icon="globe"/>
          <span :class="getLangClass('en_GB')" v-on:click="changeLanguage('en_GB')">English</span>
          /
          <span :class="getLangClass('es_ES')" v-on:click="changeLanguage('es_ES')">{{ $t('menu.spanish') }}</span>
          <div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import Dwall from '@/components/shared/logos/Dwall';
import PoweredBy from '@/components/shared/logos/PoweredBy';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import faCheck from '@fortawesome/fontawesome-free-solid/faCheck';
import faQuestionCircle from '@fortawesome/fontawesome-free-solid/faQuestionCircle';
import LoginLogoMobile from '@/components/shared/logos/LoginLogoMobile';
import LoginLogoDesktop from '@/components/shared/logos/LoginLogoDesktop';
import variables from '../stylesheets/_variables.scss';
import config from 'BRAND/config';
import {faGlobe} from '@fortawesome/fontawesome-free-solid';
import dayjs from 'dayjs';
import LoginForm from 'BRAND/components/auth/LoginForm';
import SigninForm from 'BRAND/components/auth/SigninForm';

export default {
  name: 'Login',
  components: {
    SigninForm,
    LoginForm,
    LabelInput,
    BtnPill,
    FontAwesomeIcon,
    Dwall,
    LoginLogoDesktop,
    PoweredBy,
    LoginLogoMobile
  },
  data () {
    return {
      showLogin: true,
      darkTheme: variables.theme === '"dark"'
    };
  },
  created () {
    if (this.hasSaml()) {
      // This is a dirty hack :(
      // The SAML backend does NOT WORK at first hit!
      // So the web call a dummy request and then
      // the backend works as expected o_O
      this.$http.get('/api/version');
    }
  },
  computed: {
    ...mapGetters([
      'getLocale',
      'getLanguageCode',
      'loginErrorUserNotFound',
      'loginErrorWrongPassword',
      'loginErrorExpiredLicense'
    ]),
    check () {
      return faCheck;
    },
    question () {
      return faQuestionCircle;
    },
    globe () {
      return faGlobe;
    }

  },
  methods: {
    ...mapActions([
      'setLocale',
      'login'
    ]),
    toggleShowLogin () {
      this.showLogin = !this.showLogin;
    },
    changeLanguage (locale) {
      this.setLocale({
        context: this,
        locale: locale
      });
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      this.activeLang = locale;
    },
    getLangClass (locale) {
      if (!this.activeLang) {
        this.activeLang = this.getLocale;
      }
      return this.activeLang === locale ? 'lang-active' : 'lang';
    },
    hasSaml () {
      return config.saml === true;
    },
    loginSaml () {
      location.href = '/saml/login';
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

span:hover {
  color: $primary;
  cursor: pointer;
}
@import "src/stylesheets/variables";
.login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  &:after {
    content: "";
    background-size: auto 100%;
    position: absolute;
    top: 13%;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    opacity: 1;
    @media (min-width: $screen-l) {
      background: no-repeat left center url(~BRAND/assets/login-bg.jpg);
      z-index: -3;
      background-position: left top;
      background-size: auto;
    }
  }
  &__bg {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    background-color: $login-background;
    @media (min-width: $screen-l) {
      background-color: transparent;
      background-image: radial-gradient(
          circle at 23% 35%,
          rgba($login-background, 0.5),
          rgba($login-background, 0.6),
          rgba($login-background, 0.9),
          $login-background 35%,
          $login-background
      );
    }
    @include brandCustom-loginBg();
    &:before {
      content: "";
      width: 100%;
      height: 100%;
      background-image: url($brandImage-LoginBgBefore);
      background-repeat: no-repeat;
      background-position: right bottom;
      position: absolute;
      right: 0;
      top: 0;
      opacity: 0.4;
      z-index: 0;
      @include brandCustom-loginBgBefore();
    }
  }
  &__content {
    color: $login-text-color;
    @media (max-width: $screen-l-max) {
      padding: 0 $sp-margin-large;
    }
    @media (min-width: $screen-l) {
      display: flex;
    }
  }
  &__brand {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: $sp-margin-larger;
    @media (max-width: $screen-l-max) {
      margin-top: $sp-margin-large;
      flex-direction: row;
      max-width: ($sp * 80) + $sp-margin-larger;
      padding-right: 0;
      margin-bottom: $sp-margin;
    }
  }
  .dwall {
    margin-bottom: $sp-margin;
    @media (max-width: $screen-l-max) {
      margin-bottom: $sp-margin-small;
      width: 80%;
      height: auto;
    }
  }
  .powered-by {
    width: 11rem;
    display: block;
    color: $login-powered-by-color;
    @media (max-width: $screen-l-max) {
      width: 90%;
      height: auto;
    }
  }
  &__form {
    max-width: ($sp * 80) + $sp-margin-larger;
    @media (min-width: $screen-l) {
      padding-left: $sp-margin-larger;
      border-left: 1px solid $login-form-right-border;
    }
    form {
      margin-bottom: $sp-margin;
    }
  }
  &__help {
    a {
      color: $link-dark;
      font-weight: bold;
    }
  }
  &__help-pass {
    font-weight: 700;
    a {
      color: $white;
    }
  }
}
.logo-horizontal {
  width: 800px;
  margin-bottom: $sp-margin;
  @media (max-width: $screen-l-max) {
    display: none;
  }
}
.logo-square-wrapper {
  width: 38%;
  flex-shrink: 0;
  padding-left: $sp-margin;
  border-left: 1px solid $white;
  display: flex;
  align-items: center;
  @media (min-width: $screen-l) {
    display: none;
  }
}
.logo-square {
  width: 100%;
  height: auto;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/HealthLogin.vue